import { FeedTypeEnum, IGif, IUser } from "@/common/api/services/types";
import { deviceTypeEnum, EventDevice } from "@/common/utils/device";
import { TileTarget } from "@/common/utils/routes";
import { SearchInitiator } from "@/common/store/analyticsStore";

export enum EventSource {
  Categories = "categories",
  Collections = "collections",
  Creators = "creators",
  Embed = "embed",
  Explore = "explore",
  ExploreGifs = "explore-gifs",
  ExploreImages = "explore-images",
  Favourites = "favourites",
  ForYou = "for-you",
  Trending = "trending",
  Niche = "niche",
  Niches = "niches",
  Other = "other",
  Profile = "profile",
  DirectWatch = "direct",
  Search = "search",
  SearchCreators = "search-creators",
  SearchGifs = "search-gifs",
  SearchImages = "search-images",
  SearchNiches = "search-niches",
  TagGifs = "tag-gifs",
  TagImages = "tag-images",
}

export enum SearchResultSection {
  Gif = "gif",
  Image = "image",
  Niche = "niche",
  Creator = "creator",
  Tag = "tag",
}

export interface GifViewEvent {
  gifId: string;
  tags: string[];
  source: EventSource;
  sourceId: string | null;
  position: number;
  boosted: boolean;
  promoted: boolean;
  userName?: string | null;
  fullscreen: boolean;
  orientation?: string;
  context?: string | null;
  reason?: string | null;
}

export interface MeaningfulGifViewEvent {
  gifId: string;
  percent: number;
  source: EventSource;
}

export interface GifShareEvent {
  gif: string;
}

export interface InfoBarEvent {
  announcement: string;
}

export type WatchPageNavigationSource = "related_tags" | "player_chips" | "creator_link" | "niche_widget";

export interface WatchPageNavigationEvent {
  elementType: string;
  targetURL: string;
  source: WatchPageNavigationSource;
}

export interface GifPlaybackEvent {
  duration: number;
  gif: string;
  gifDuration?: number;
  source: EventSource;
  sourceId: string | null;
  userName?: string | null;
  percent: number;
  boosted: boolean;
  position?: number | null;
  playCount: number;
  startPoint: number;
}

export interface GifReplayEvent {
  gifId: string;
  tags: string[];
}

export interface GifVideoLoadEvent {
  duration: number;
  source: EventSource;
}

export interface UploadStartedEvent {
  verified: boolean;
  username: string;
}

export interface UploadEndedEvent {
  verified: boolean;
  username: string;
}

export interface UploadSelectedEvent {
  type: "image" | "video" | "bulkImages" | "url";
  verified: boolean;
  username: string;
}

export interface UploadSourceConfirmedEvent {
  type: "image" | "video" | "multiple";
  verified: boolean;
  username: string;
}

export interface UploadSourceDroppedEvent {
  type: "image" | "video" | "images";
  verified: boolean;
  username: string;
}

export interface UploadAudienceDefinedEvent {
  verified: boolean;
  username: string;
}

export interface UploadVideoTrimmedEvent {
  verified: boolean;
  username: string;
}

export interface UploadTagSelectionStartedEvent {
  verified: boolean;
  username: string;
}

export interface UploadTagSelectionEndedEvent {
  count: number;
  verified: boolean;
  username: string;
}

export interface GifClickedEvent {
  source: EventSource;
  position: number;
}

export interface GifLikedEvent {
  gifId: string;
  source: EventSource;
  sourceId: string | null;
  boosted: boolean;
  position: number;
  playCount: number;
  isMainGIF: boolean;
  userName?: string | null;
}

export interface GifUnlikedEvent {
  gifId: string;
  isMainGIF: boolean;
}

export interface GifSharedEvent {
  gifId: string;
  source: EventSource;
  sourceId: string | null;
  boosted: boolean;
  position?: number;
  playCount?: number;
  userName?: string | null;
}

export interface AddedToCollectionEvent {
  gifId: string;
  source: EventSource;
  sourceId: string | null;
  boosted: boolean;
  position?: number;
  playCount?: number;
  userName?: string | null;
}

export interface AdLoadedEvent {
  isLoaded: boolean;
}

export type MenuButton = "home" | "explore" | "niches" | "login" | "profile" | "upload" | "studio";

export interface MenuButtonClickedEvent {
  name: string;
  label: string;
  button: MenuButton;
  device: EventDevice;
  source_page: string;
}

export interface FollowUserClickedEvent {
  userName: string;
  source: EventSource;
  sourceId: string | null;
  gifId?: string;
  position?: number;
  playCount?: number;
}

export interface UnfollowUserClickedEvent {
  userName: string;
  source: EventSource;
  sourceId: string | null;
}

export interface FollowUserClickedAnonymouslyEvent {
  userName: string;
  source: EventSource;
  sourceId: string | null;
}

export interface SocialProfileClickedEvent {
  userName: string;
  platform: string | null;
  originGif?: IGif;
}

export interface PaidLinksClickedEvent {
  username: string;
  source: EventSource;
  source_id?: string | null;
}

export interface ProfileClickedEvent {
  gifId: string;
  creator: string;
  source: EventSource;
  sourceId: string | null;
  position: number;
  playCount?: number;
  userName?: string | null;
  promoted: boolean;
}

export interface TagClickedEvent {
  tag: string;
  section: string;
}

export interface SearchedEvent {
  query: string;
  totalCount: number;
}

export type QueryType = "Manual" | "Tag" | "User" | "Niche" | "Historic";

interface SearchQuerySubmittedEvent {
  query: string;
  queryType: QueryType;
  queryCount: number;
  itemId?: string;
}

export interface UploadFinishedEvent {
  sourceURL?: "local" | "url";
  verified: boolean;
  username: string;
}

export interface ProfileViewedEvent {
  userName: string;
}

export interface UserFetchedEvent {
  user: IUser;
}

export interface ExperimentVariantExposureEvent {
  experimentKey: string;
  experimentVariant: string | boolean;
}

export interface FirstVisitEvent {
  path: string;
  timestamp: number;
}

export interface DailyVisitEvent {
  username?: string;
  path: string;
}

export interface managePageVisitedEvent {
  username: string;
  verified: boolean;
}

export interface DeviceTrackedEvent {
  deviceId: string;
  deviceType: deviceTypeEnum;
}

export interface SearchResultClickedEvent {
  section: SearchResultSection;
  title: string;
  position: number;
  initiator: SearchInitiator | null;
}

export interface SeeAllButtonClickedEvent {
  section?: SearchResultSection;
  initiator: SearchInitiator | null;
}

interface PageClosedEvent {
  page: "watch" | "gifs" | "niche";
  type: string;
  gifsViewed: number;
  gifsViewedMeaningful: number;
  timeSpent: number;
  contentLength: number;
  searchTag?: string;
}

interface OrientationChangedEvent {
  type: string;
}

export interface NicheGifVoteUpClickedEvent {
  gif: string;
  niche: string;
}

export interface NicheGifVoteDownClickedEvent {
  gif: string;
  niche: string;
}

export interface JoinNicheClickedEvent {
  nicheId: string;
}

export interface JoinNicheClickedAnonymouslyEvent {
  nicheId: string;
}

export interface LeaveNicheClickedEvent {
  nicheId: string;
}

export type NicheDiscoverySource = "all_niches";

export interface NicheDiscoveryEvent {
  nicheId: string;
  source: NicheDiscoverySource;
}

export type NicheSubscriptionSource = "old_button" | "subscription_module";

export interface NicheSubscribedEvent {
  nicheId: string;
  origin: NicheSubscriptionSource;
  source: NicheDiscoverySource | null;
}

export interface NicheUnsubscribedEvent {
  nicheId: string;
  origin: NicheSubscriptionSource;
}

export interface NicheSubscriptionModuleShownEvent {
  nicheId: string;
}

export type NichePreviewClickInitiator = "meta" | "arrow button" | "watch button" | "join button" | "gif";

export interface NichePreviewClickedEvent {
  nicheId: string;
  source: EventSource;
  initiator: NichePreviewClickInitiator;
}

export interface ManageItemInteractedEvent {
  id: string;
  interaction: string;
  username: string;
  verified: boolean;
}

export interface ManagePageInteractedEvent {
  interaction: string;
  username: string;
  verified: boolean;
}

export interface BoostProfileClickedEvent {
  campaignId: string;
}

export interface NicheGifsOrderChangedEvent {
  nicheId: string;
  order: string;
}

export interface LiveCircleClickedEvent {
  deviceType: deviceTypeEnum;
}

export interface NicheCategoryClickedEvent {
  category: string;
  position?: number;
}

export interface LiveCamClickedEvent {
  creator: string;
  video: string;
  position: number;
}

export interface LiveCamViewEvent {
  creator: string;
  video: string;
  position: number;
}

export interface PaidLinksViewedEvent {
  username: string;
  source: EventSource;
  source_id?: string | null;
}

export interface OfModuleShownEvent {
  placement: "module" | "skyscraper";
  device: EventDevice;
  source: EventSource;
}

export interface GridNewTabOpenedEvent {
  target: TileTarget;
  gif?: string;
  position: number;
  method: "middle_click" | "modifier_click";
  device: EventDevice;
  source: EventSource;
}

export interface PageNewTabOpenedEvent {
  device: EventDevice;
}

export interface EmbedRevenueLinkClickedEvent {
  username: string;
  platform: string | null;
  gif: string;
}

export type AVPromptSource = "immediate" | "delayed";

export type ageCommonEvent = { country?: string | null; state?: string | null; source?: AVPromptSource };

export interface RegistrationAttributionEvent {
  source?: string;
}

export interface FeedSwitchedEvent {
  src: FeedTypeEnum;
  dest: FeedTypeEnum;
}

export type GoToTopSource = "profile_overview";

export interface GoToTopEvent {
  source: GoToTopSource;
  creator: string;
}

export interface PlayerSocialLinkClickedEvent {
  creator: string;
  platform: string | null;
  gif: string;
}

export type RevenueLinkType = "embed" | "profile" | "player";

export interface CsatSurveyEvent {
  version: string;
}

export type RedgifsAiSpot = "desktop tab" | "desktop burger menu" | "mobile burger menu";

export interface RedgifsAiClickedEvent {
  spot: RedgifsAiSpot;
}

export type BusEvents = {
  gifView: GifViewEvent;
  meaningfulGifView: MeaningfulGifViewEvent;
  gifPlayback: GifPlaybackEvent;
  gifReplay: GifReplayEvent;
  gifVideoLoad: GifVideoLoadEvent;

  forceFullscreenEntered: undefined;
  forceFullscreenExited: undefined;
  fullscreenEntered: undefined;
  fullscreenExited: undefined;

  watchPageVisited: undefined;
  firstVisit: FirstVisitEvent;
  dailyVisit: DailyVisitEvent;
  homePageVisited: undefined;
  managePageVisited: managePageVisitedEvent;

  gifClicked: GifClickedEvent;
  gifLiked: GifLikedEvent;
  gifUnliked: GifUnlikedEvent;
  gifShared: GifSharedEvent;
  addedToCollection: AddedToCollectionEvent;

  adLoaded: AdLoadedEvent;

  infoBarClicked: InfoBarEvent;
  infoBarClosed: InfoBarEvent;

  sharedFixedEmbed: undefined;
  sharedResponsiveEmbed: undefined;
  shareClick: undefined;
  shareCopy: GifShareEvent;
  shareDiscord: GifShareEvent;
  shareEmbed: undefined;
  shareReddit: GifShareEvent;
  shareTwitter: GifShareEvent;
  optionClicked: undefined;
  pinnedContentClicked: undefined;
  menuButtonClicked: MenuButtonClickedEvent;
  followUserClicked: FollowUserClickedEvent;
  followUserClickedAnonymously: FollowUserClickedAnonymouslyEvent;
  unfollowUserClicked: UnfollowUserClickedEvent;
  socialProfileClicked: SocialProfileClickedEvent;
  profileClicked: ProfileClickedEvent;
  boostProfileClicked: BoostProfileClickedEvent;
  tagClicked: TagClickedEvent;

  signUpSuccess: RegistrationAttributionEvent;
  loginCompleted: undefined;
  loggedOut: undefined;

  searchBarOpened: undefined;
  searched: SearchedEvent;
  searchResultClicked: SearchResultClickedEvent;
  searchQuerySubmitted: SearchQuerySubmittedEvent;
  seeAllButtonClicked: SeeAllButtonClickedEvent;

  tagFeedbackEnabledOptions: undefined;

  uploadSelected: UploadSelectedEvent;
  uploadStarted: UploadStartedEvent;
  uploadTagSelectionStarted: UploadTagSelectionStartedEvent;
  uploadTagSelectionEnded: UploadTagSelectionEndedEvent;
  uploadEnded: UploadEndedEvent;
  uploadVideoTrimmed: UploadVideoTrimmedEvent;
  uploadSourceConfirmed: UploadSourceConfirmedEvent;
  uploadAudienceDefined: UploadAudienceDefinedEvent;
  uploadSourceDropped: UploadSourceDroppedEvent;
  uploadFinished: UploadFinishedEvent;

  gifWatched120s: undefined;
  profileViewed: ProfileViewedEvent;
  voteUpAddClicked: undefined;
  voteUpRemoveClicked: undefined;
  voteDownAddClicked: undefined;
  voteDownRemoveClicked: undefined;
  privateSessionStarted: undefined;

  PWASessionStarted: undefined;
  PWAPageVisited: undefined;

  userFetched: UserFetchedEvent;
  deviceTracked: DeviceTrackedEvent;

  experimentVariantExposure: ExperimentVariantExposureEvent;

  watchPageNavigation: WatchPageNavigationEvent;

  playerNicheClicked: undefined;
  playerTagClicked: undefined;

  verificationSubmit: undefined;

  pageClosed: PageClosedEvent;

  orientationChanged: OrientationChangedEvent;

  nicheGifVoteUpClicked: NicheGifVoteUpClickedEvent;
  nicheGifVoteDownClicked: NicheGifVoteDownClickedEvent;
  nicheGifsOrderChanged: NicheGifsOrderChangedEvent;
  joinNicheClicked: JoinNicheClickedEvent;
  joinNicheClickedAnonymously: JoinNicheClickedAnonymouslyEvent;
  leaveNicheClicked: LeaveNicheClickedEvent;
  nicheUnsubscribed: NicheUnsubscribedEvent;
  nicheSubscriptionModuleShown: NicheSubscriptionModuleShownEvent;
  nichePreviewClicked: NichePreviewClickedEvent;
  allNichesPageViewed: undefined;
  nicheOpened: NicheDiscoveryEvent;
  nicheSubscribed: NicheSubscribedEvent;

  manageItemInteracted: ManageItemInteractedEvent;
  managePageInteracted: ManagePageInteractedEvent;

  liveCircleClicked: LiveCircleClickedEvent;
  nicheCategoryClicked: NicheCategoryClickedEvent;
  liveCamClicked: LiveCamClickedEvent;
  liveCamView: LiveCamViewEvent;

  paidLinksClicked: PaidLinksClickedEvent;
  paidLinksViewed: PaidLinksViewedEvent;
  embedRevenueLinkClicked: EmbedRevenueLinkClickedEvent;

  ofModuleShown: OfModuleShownEvent;

  gridNewTabOpened: GridNewTabOpenedEvent;
  pageNewTabOpened: PageNewTabOpenedEvent;

  ageVerificationStarted: ageCommonEvent;
  ageVerificationCompleted: ageCommonEvent;
  ageVerificationAbandoned: ageCommonEvent;
  ageGateRedirected: ageCommonEvent;
  ageVerificationPromptShown: ageCommonEvent;
  ageVerificationFailed: ageCommonEvent;
  ageVerificationLiteStayed: ageCommonEvent;
  enterRedgifsClicked: ageCommonEvent;

  csatSurveyShown: CsatSurveyEvent;
  csatSurveySkipped: CsatSurveyEvent;
  csatSurveySubmitted: CsatSurveyEvent;

  registrationBannerImpression: undefined;
  registrationBannerClick: undefined;
  registrationBannerClosed: undefined;
  registrationStarted: RegistrationAttributionEvent;
  registrationSubmitted: RegistrationAttributionEvent;
  registrationVerificationCodeSent: RegistrationAttributionEvent;

  feedSwitched: FeedSwitchedEvent;

  profileGoToTopShown: GoToTopEvent;
  profileGoToTopClicked: GoToTopEvent;

  playerSocialLinkClicked: PlayerSocialLinkClickedEvent;

  redgifsAiClicked: RedgifsAiClickedEvent;
};
